// Pure merge helpers for the registry "merge / same person" gesture
// (spec §4). Folding the SOURCE row into the TARGET row: the target keeps
// its primary (display_name / term); the source's primary + all of the
// source's aliases become aliases on the target. The result is the union
// of [target.aliases, source primary, source.aliases], deduped
// case-insensitively, with the target's own primary excluded (a row never
// lists itself as an alias).
//
// No DB access — the caller writes merged.aliases to the target row and
// soft-deletes the source. Kept pure so the dedup/casefold logic is
// unit-testable in isolation.

export interface MergeableAttendee {
  display_name: string;
  aliases?: string[] | null;
}

export interface MergeableTerm {
  term: string;
  aliases?: string[] | null;
}

// Case-insensitive union that preserves first-seen casing + order. Drops
// blanks and any candidate equal (casefold) to `excludePrimary`.
function unionAliases(
  candidates: (string | null | undefined)[],
  excludePrimary: string,
): string[] {
  const exclude = excludePrimary.trim().toLowerCase();
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of candidates) {
    const value = (raw ?? "").trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (key === exclude || seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out;
}

export function mergeAttendee(
  target: MergeableAttendee,
  source: MergeableAttendee,
): { display_name: string; aliases: string[] } {
  const aliases = unionAliases(
    [...(target.aliases ?? []), source.display_name, ...(source.aliases ?? [])],
    target.display_name,
  );
  return { display_name: target.display_name, aliases };
}

// Append a single recognized variant spelling onto a term's alias list.
// Same casefold-union + self-exclusion rules as a merge, for one spelling.
export function addAliasToTerm(
  target: MergeableTerm,
  variant: string,
): { term: string; aliases: string[] } {
  const aliases = unionAliases([...(target.aliases ?? []), variant], target.term);
  return { term: target.term, aliases };
}

export function mergeTerm(
  target: MergeableTerm,
  source: MergeableTerm,
): { term: string; aliases: string[] } {
  const aliases = unionAliases(
    [...(target.aliases ?? []), source.term, ...(source.aliases ?? [])],
    target.term,
  );
  return { term: target.term, aliases };
}
